import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSetSession } from '@/hooks/useAuth';
import type { AuthUser } from '@monitoring-panel/types';

const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL || 'https://auth.advia.tech';

export function AuthCallback() {
  const [searchParams] = useSearchParams();
  const setSession = useSetSession();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setError('No authentication token provided');
      return;
    }

    async function validateAndStoreToken(token: string) {
      try {
        const response = await fetch(`${AUTH_SERVICE_URL}/session`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Invalid token');
        }

        const data = await response.json();

        setSession({
          token,
          user: data.user as AuthUser,
          expiresAt: data.session?.expiresAt,
        });

        navigate('/executions', { replace: true });
      } catch (err) {
        setError('Failed to validate authentication token');
        console.error('Auth validation error:', err);
      }
    }

    validateAndStoreToken(token);
  }, [searchParams, setSession, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-adv-sunken">
        <div className="text-center">
          <h1 className="adv-page-title mb-4">Authentication Error</h1>
          <p className="adv-section-desc mb-6">{error}</p>
          <a
            href={AUTH_SERVICE_URL}
            className="adv-btn adv-btn--primary"
          >
            Try Again
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-adv-sunken">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-adv-gold mx-auto mb-4"></div>
        <p className="adv-section-desc">Authenticating...</p>
      </div>
    </div>
  );
}
